import { FeatureToggle } from 'app/common/feature-toggle'
import { UnderlinedText } from 'app/common/underlined-text'
import {
    OrderTimeline,
    PrepareOrderButton
} from 'app/ecommerce/common/components'
import React from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import type { CacOrder, DeviceCheck } from 'shared/types'
import {
    ecommerceType,
    ecommOrderStatus,
    featureCodes,
    orderStatus
} from 'shared/utils'
import {
    isLaunchedOrderPreparing,
    isLaunchedOrderReadyForCollect,
    isLaunchedOrderWaitingForResponse,
    isOrderCancelled,
    isOrderDelivered,
    isOrderLaunched
} from 'shared/utils/ecommerce'
import styled from 'styled-components'
import { Spinner } from '../../../../assets/icons'
import { COLOR } from '../../../../../app-v2/design-tokens'

const { DONE, INPROGRESS, READY, WAITING_RESPONSE } = orderStatus
const {
    PAID,
    PREPARED_NO_CALLBACK,
    REBOOT_ERROR,
    PAYMENT_PENDING
} = ecommOrderStatus
const { TABLE_ORDER } = ecommerceType

type Props = {
    order: CacOrder,
    fromList?: boolean,
    noBottomMargin?: boolean,
    onSuccess?: () => void,
    onClick?: () => void,
    cancelOrder?: (
        id: string,
        token: string,
        deviceCheckPayload: DeviceCheck
    ) => void,
    alreadyClicked?: boolean
}

export const OrderPreparation = ({
    order,
    fromList,
    noBottomMargin,
    onSuccess,
    onClick,
    cancelOrder,
    alreadyClicked
}: Props) => {
    const intl = useIntl()
    const {
        id,
        token,
        orbStatus,
        orderStatus,
        pickUpType,
        restaurant,
        deliveredAt
    } = order

    const { secondsBeforeOrderDelivered } = useSelector(
        (state: RootState) => state.applicationState
    )

    const isLaunched = isOrderLaunched(orderStatus, orbStatus)
    const isCancelled = isOrderCancelled(orderStatus)
    const isDelivered = isOrderDelivered(
        orbStatus,
        deliveredAt,
        secondsBeforeOrderDelivered
    )
    const isTableOrder = pickUpType === TABLE_ORDER

    const getTimelineStep = () => {
        const isWaitingForResponse = isLaunchedOrderWaitingForResponse(
            orbStatus
        )
        const isPreparingInRestaurant = isLaunchedOrderPreparing(
            orbStatus,
            orderStatus
        )
        const isReadyForCollect = isLaunchedOrderReadyForCollect(
            orbStatus,
            isDelivered
        )

        const isStep4 = isDelivered
        const isStep3 = isReadyForCollect || isStep4
        const isStep2 = isPreparingInRestaurant || isStep3
        const isStep1 = (isWaitingForResponse || isStep2) && !isCancelled

        return isStep4 ? 4 : isStep3 ? 3 : isStep2 ? 2 : isStep1 ? 1 : 0
    }

    const isWaitingForResponse = isLaunchedOrderWaitingForResponse(orbStatus)

    const getTimelineKey = () => {
        switch (orbStatus) {
            case WAITING_RESPONSE:
                return 'ecommerce.timeline.pending'
            case INPROGRESS:
                return 'ecommerce.timeline.preparing'
            case READY:
                return isTableOrder
                    ? 'ecommerce.timeline.tableorder.ready'
                    : 'ecommerce.timeline.clickandcollect.ready'
            case DONE:
                return isDelivered
                    ? isTableOrder
                        ? 'ecommerce.timeline.delivered.tableorder'
                        : 'ecommerce.timeline.delivered'
                    : isTableOrder
                    ? 'ecommerce.timeline.tableorder.ready'
                    : 'ecommerce.timeline.clickandcollect.ready'
            default:
                return orderStatus === PREPARED_NO_CALLBACK ||
                    orderStatus === REBOOT_ERROR ||
                    !orbStatus
                    ? 'ecommerce.timeline.error.no_merin_response'
                    : 'ecommerce.timeline.preparing'
        }
    }

    const { pathname } = useSelector(
        (state: RootState) => state.router.location
    )
    const hideCancel =
        pathname ===
        intl.formatMessage({
            id: 'routes.clickandcollect.cart.confirmation'
        })

    return (
        <MainContainer>
            {isLaunched || isCancelled ? (
                <OrderTimeline
                    step={getTimelineStep()}
                    descriptionKey={getTimelineKey()}
                />
            ) : isTableOrder ? (
                <StyledSpinnerContainer>
                    <Spinner color={COLOR.PRIMARY} />
                </StyledSpinnerContainer>
            ) : (
                orderStatus === PAID && (
                    <Container>
                        <PrepareOrderButton
                            onSuccess={onSuccess}
                            token={token}
                            collectMode={pickUpType}
                            restaurant={restaurant}
                            fromList={fromList}
                            noBottomMargin={noBottomMargin}
                            noDisplay={isLaunched}
                            alreadyClicked={alreadyClicked}
                        />
                        {fromList && !hideCancel && (
                            <FeatureToggle
                                feature={featureCodes.CLICK_AND_COLLECT}>
                                {!!id &&
                                    !!token &&
                                    !!cancelOrder &&
                                    !!onClick && (
                                        <CancelContainer
                                            onClick={onClick}
                                            textKey="clickandcollect.order.cancel"
                                        />
                                    )}
                            </FeatureToggle>
                        )}
                    </Container>
                )
            )}
        </MainContainer>
    )
}

const MainContainer = styled.div`
    display: block;
    width: 100%;
    margin: auto;
    max-width: 400px;
`

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`

const CancelContainer = styled(UnderlinedText)`
    display: inline-block;
    cursor: pointer;
    margin: 6px auto 10px;
    justify-content: center;
`

const StyledSpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`
